import { ReactComponent as DownloadIcon } from '@vkontakte/icons/src/svg/16/download_outline_16.svg';
import { ReactComponent as ViewIcon } from '@vkontakte/icons/src/svg/16/view_outline_16.svg';
import classNames from 'clsx';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { downloadAndViewCounterTariff } from 'reactApp/appHelpers/featuresHelpers/features/downloadAndViewCounterTariff';
import { getAlbumItemWeblinkDownloadsAndViewsCount, getCurrentAlbum } from 'reactApp/modules/albums/albums.selector';
import { getCurrentFolderHome, getHomeItemWeblinkDownloadsAndViewsCount } from 'reactApp/modules/home/home.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isPublicFolder } from 'reactApp/modules/storage/folder.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import type { RootState } from 'reactApp/store';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { isDarkThemeModeEnabled } from 'reactApp/utils/theme';

import styles from './DownloadAndViewCounter.css';

export const DownloadAndViewCounter = memo(() => {
    const dividerRef = useRef<HTMLDivElement | null>(null);

    const storage = useSelector(getCurrentStorage);
    const currentAlbum = useSelector(getCurrentAlbum);
    const currentFolder = useSelector(getCurrentFolderHome);
    const isPublicFold = isPublicFolder(currentFolder);

    const { downloads, views } = useSelector((state: RootState) =>
        storage === EStorageType.albums
            ? getAlbumItemWeblinkDownloadsAndViewsCount(state, currentAlbum?.id)
            : getHomeItemWeblinkDownloadsAndViewsCount(state, currentFolder?.id)
    );

    const containsDarkTheme = isDarkThemeModeEnabled();

    const [showViewsInfo, setShowViewsInfo] = useState(false);
    const [showDownloadsInfo, setShowDownloadsInfo] = useState(false);
    const [analyticsSent, setAnalyticsSend] = useState(false);

    useEffect(() => {
        if (
            downloadAndViewCounterTariff &&
            downloadAndViewCounterTariff.variant !== 'a' &&
            downloads !== undefined &&
            views !== undefined &&
            !analyticsSent
        ) {
            sendPaymentGa({
                eventCategory: ECategoryGa.counter,
                action: 'downloads',
                count: downloads,
            });
            sendPaymentGa({
                eventCategory: ECategoryGa.counter,
                action: 'views',
                count: views,
            });

            setAnalyticsSend(true);
        }
    }, [downloads, views, analyticsSent]);

    if (
        !downloadAndViewCounterTariff ||
        downloadAndViewCounterTariff?.variant === 'a' ||
        (!isPublicFold && storage !== EStorageType.albums)
    ) {
        return null;
    }

    return (
        <>
            <div
                className={classNames(styles.container, {
                    [styles.container_dark]: containsDarkTheme,
                })}
            >
                <div className={styles.item} onMouseOver={() => setShowViewsInfo(true)} onMouseLeave={() => setShowViewsInfo(false)}>
                    <ViewIcon />
                    {views || 0}
                    {showViewsInfo && <div className={styles.itemInfo}>Просмотров</div>}
                </div>
                <div ref={dividerRef} className={styles.divider} />
                <div
                    className={styles.item}
                    onMouseOver={() => setShowDownloadsInfo(true)}
                    onMouseLeave={() => setShowDownloadsInfo(false)}
                >
                    <DownloadIcon />
                    {downloads || 0}
                    {showDownloadsInfo && <div className={styles.itemInfo}>Скачиваний</div>}
                </div>
            </div>
        </>
    );
});

DownloadAndViewCounter.displayName = 'DownloadAndViewCounter';
